import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import debounce from "lodash/debounce"
import { useLocation } from "@reach/router"

import RenderSearch from "../components/Search/RenderSearch"

const useCreateContext = () => {
	const location = useLocation()
	const [active, setActive] = useState(false)
	const [query, _setQuery] = useState<string | undefined>()
	const mounted = useRef(true)

	useEffect(() => {
		if (active) {
			return
		}

		_setQuery(undefined)
	}, [active])

	// close on navigation
	useEffect(() => {
		setActive(false)
	}, [location])

	useEffect(() => {
		return () => {
			mounted.current = false
		}
	}, [])

	const openSearch = useCallback(() => {
		setActive(true)
	}, [])

	const closeSearch = useCallback(() => {
		setActive(false)
	}, [])

	const setQuery = useMemo(() => debounce(_setQuery, 300), [])

	return {
		active,
		openSearch,
		closeSearch,

		query,
		setQuery,
	}
}

const GlobalSearchContext = createContext<ReturnType<
	typeof useCreateContext
> | null>(null)

export const useGlobalSearchContext = () => {
	const ctx = useContext(GlobalSearchContext)

	if (ctx === null) {
		throw new Error("No GlobalSearchContextProvider available.")
	}

	return ctx
}

export const GlobalSearchContextProvider: React.FC = ({ children }) => {
	const ctx = useCreateContext()

	return (
		<GlobalSearchContext.Provider value={ctx}>
			{children}
			<RenderSearch />
		</GlobalSearchContext.Provider>
	)
}
